/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
	Box,
	Dialog,
	Paper,
	CircularProgress,
	TextField,
	Button,
} from "@material-ui/core";
import AppBarComponent from "../../../Common/Headers/AppBarComponent";
import { Transition } from "../../../../utils/ModalHelpers";
import DelayedRedirect from "../../../../utils/DelayedRedirect";
import EnhancedDropdownComponent from "../../../Common/Dropdowns/EnhancedDropdownComponent";
import DatePickerComponent from "../../../Common/DatePickers/DatePickerComponent";
import { GetSortedOptionsByName } from "../../../../services/helpers";
import {
	getBrands,
	createChecklistTemplate,
	getChecklistTemplateById,
	updateChecklistTemplateHeaderImage,
} from "./services/checklistTemplatesApisService";
import { useHistory, useLocation } from "react-router-dom";
import CheckboxComponent from "../../../Common/Checkboxes/CheckboxComponent";
import { fetchRoles } from "../../../../services/OutletUsers/outletUserService";
import {
	DisplayMessage,
	GetFileExtension,
	Uuidv4,
} from "../../../../services/helpers";
import {
	CheckIfSasIsValid,
	GenerateSAS,
	UploadFileInStorage,
} from "../../../../services/uploadHelpers";
import ImageUpload from "../../../Common/Images/ImageUpload";
import SegmentsCreation from "./SegmentsCreation";
import {
	buildChecklistTemplateRequest,
	buildSegmentsPayload,
} from "./utils/requestBuilders";
import {
	mapTemplateToFormData,
	mapSegmentsToDnD,
} from "./utils/responseMappers";
import { validateChecklistTemplate } from "./utils/formValidators";
import ModifiedByInfo from "../../../Common/Footers/ModifiedByInfo";
import LanguageSwitch from "../../../Translations/LanguageSwitch";
import { objectTypes, templateTypes } from "./utils/constants";
import { checklistTranslationsFullMapper } from "./mappers/checklistTranslationsFullMapper";
import { languages } from "./utils/constants";
import { computeLanguageCompletion } from "../../../Translations/utils/helpers";

export default function ChecklistTemplatesCreation(props) {
	const { resources, defaultObjectType, templateId, hasTranslationLicence } = props;
	const [dialogOpen, setDialogOpen] = useState(true);
	const [templateWasCreated, setTemplateWasCreated] = useState(false);
	const [templateWasUpdated, setTemplateWasUpdated] = useState(false);
	const [translationsData, setTranslationsData] = useState([]);
	const [translationsPercentages, setTranslationsPercentages] = useState([]);
	const [redirect, setRedirect] = useState(null);
	const [brands, setBrands] = useState([]);
	const [roles, setRoles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [dataInitialized, setDataInitialized] = useState(false);
	const [formData, setFormData] = useState({
		selectedType: defaultObjectType,
		brand: 0,
		validFrom: "",
		validTill: "",
		operationCode: "",
		description: "",
		isAutosuggest: false,
		isActive: true,
		isB2CExcluded: false,
		isMandatory: false,
		signatureOutletUserRoleID1: 0,
		signatureOutletUserRoleID2: 0,
		modified: {
			on: "",
			byLastName: "",
			byFirstName: "",
			avatar: "",
		},
		created: {
			on: "",
			byLastName: "",
			byFirstName: "",
			avatar: "",
		},
	});
	const location = useLocation();
	const [lang, setLang] = useState("BASE");
	const isEditMode = !!templateId;
	const [headerImageFile, setHeaderImageFile] = useState(null);
	const [headerImageData, setHeaderImageData] = useState(null);
	const [initialHeaderImageData, setInitialHeaderImageData] = useState(null);

	const handleLanguageChange = (newLang) => {
		setLang(newLang);
	};

	const [segments, setSegments] = useState({});
	const [segmentData, setSegmentData] = useState({});

	const baseAndLanguages = useMemo(() => {
		return [{ code: "BASE", label: resources["BaseText"] }, ...languages];
	}, [resources]);

	const fetchTemplateDataById = useCallback(
		async (hasLoadingState = true) => {
			if (!templateId) {
				return;
			}

			if (hasLoadingState) {
				setLoading(true);
			}

			const template = await getChecklistTemplateById(templateId);
			if (template) {
				const mappedChecklistTranslations =
					checklistTranslationsFullMapper(template);
				setTranslationsData(mappedChecklistTranslations);

				setTranslationsPercentages(
					computeLanguageCompletion(
						baseAndLanguages,
						mappedChecklistTranslations
					)
				);

				setFormData(mapTemplateToFormData(template));

				if (template.headerImage) {
					const imageData = { imageUri: template.headerImage };
					setHeaderImageData(imageData);
					setInitialHeaderImageData(imageData);
				} else {
					setHeaderImageData(null);
					setInitialHeaderImageData(null);
				}
				// Reset header image file when template is loaded
				setHeaderImageFile(null);

				const { dndItems, dndSegmentMetadata } = mapSegmentsToDnD(
					template.segments
				);

				setSegments(dndItems);
				setSegmentData(dndSegmentMetadata);

				if (hasLoadingState) {
					setLoading(false);
					setDataInitialized(true);
					setTemplateWasUpdated(false);
				}
				return dndItems;
			} else {
				DisplayMessage(resources["TemplateNotFound"], "error");
				setRedirect("/checklisttemplates");
			}

			if (hasLoadingState) {
				setLoading(false);
			}
		},
		[templateId, resources, baseAndLanguages]
	);

	useEffect(() => {
		const fetchRolesAndTemplate = async () => {
			setLoading(true);

			const rolesSorted = await fetchRoles();
			rolesSorted.unshift({ id: 0, description: resources["Select"] });
			setRoles(rolesSorted);

			if (isEditMode) {
				await fetchTemplateDataById();
			} else {
				setDataInitialized(true);
			}
			setLoading(false);
		};

		fetchRolesAndTemplate();
	}, [resources, templateId, fetchTemplateDataById, isEditMode]);

	useEffect(() => {
		const fetchBrandsData = async () => {
			const response = await getBrands(formData.selectedType);
			let fetchedBrands = [];
			if (response) {
				fetchedBrands = response.map((brand) => ({
					id: brand.id,
					description: brand.description,
					objectType: brand.objectType,
				}));
			}

			const defaultBrand = {
				id: 0,
				description: resources["All"],
				objectType: null,
			};

			setBrands([defaultBrand, ...fetchedBrands]);

			// Only update the brand with the default value if we're in creation mode.
			if (!isEditMode) {
				setFormData((prev) => ({
					...prev,
					brand: defaultBrand.id,
				}));
			}
		};

		fetchBrandsData();
	}, [formData.selectedType, resources, isEditMode]);

	useEffect(() => {
		if (isEditMode && location.state?.refreshTemplate) {
			fetchTemplateDataById();
		}
	}, [location, fetchTemplateDataById, isEditMode]);

	const handleClose = () => {
		setDialogOpen(false);
		setRedirect("/checklisttemplates");
	};

	const handleFormChange = (key, value) => {
		setFormData((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const handleDateChange = (key, value) => {
		setFormData((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const handleHeaderImageFileChange = async (files) => {
		const firstFile = files.length > 0 ? files[0] : null;
		setHeaderImageFile(firstFile);
	};

	const removeHeaderImage = async () => {
		setHeaderImageData(null);
		setHeaderImageFile(null);
	};

	const uploadFile = async (file, body, error) => {
		const sas = await GenerateSAS(body);
		if (!CheckIfSasIsValid(sas)) {
			DisplayMessage(resources["ErrorUploadingFileToStorage"], "error");
			return null;
		}
		const blobPath = await UploadFileInStorage(
			file,
			sas[0],
			"image/png",
			error
		);
		return blobPath;
	};

	const handleSaveChecklistTemplateHeaderImage = async (templateId, fileToUpload = null) => {
		const file = fileToUpload || headerImageFile;
		if (templateId < 1 || !file) {
			return;
		}

		const fileExtension = GetFileExtension(file.name);
		const sasRequest = JSON.stringify({
			minutes: 5,
			entityType: "checklisttemplateheader",
			entityValue: parseInt(templateId),
			fileNames: [`${Uuidv4()}.${fileExtension}`],
		});

		const blobPath = await uploadFile(
			file,
			sasRequest,
			resources["ErrorOnLoadingFile"]
		);

		if (!blobPath) {
			return;
		}

		const result = await updateChecklistTemplateHeaderImage(templateId, blobPath);
		if (result && result.headerImage) {
			const imageData = { imageUri: result.headerImage };
			setHeaderImageFile(null);
			setHeaderImageData(imageData);
			setInitialHeaderImageData(imageData);
			DisplayMessage(resources["ChecklistTemplateHeaderImageWasSaved"], "success");
		}
	};

	const [touchedFields, setTouchedFields] = useState({
		description: false,
		validFrom: false,
	});

	const handleFieldBlur = (fieldName) => {
		setTouchedFields((prev) => ({
			...prev,
			[fieldName]: true,
		}));
	};

	const setAllFieldsAsTouched = () => {
		setTouchedFields({
			description: true,
			validFrom: true,
		});
	};

	// Calculate error states for required fields
	const descriptionError =
		touchedFields.description && !formData.description.trim();
	const validFromError = touchedFields.validFrom && !formData.validFrom;

	const [validateSegmentsAndItems, setValidateSegmentsAndItems] =
		useState(null);

	const handleSubmit = async (
		hasLoadingState = true,
		refetchTemplate = true,
		showSuccessToasts = true
	) => {
		setAllFieldsAsTouched();

		// Use timestamp instead of toggle to guarantee a new value each time
		setValidateSegmentsAndItems(Date.now());

		const errors = validateChecklistTemplate({
			formData,
			segments,
			segmentData,
			resources,
		})

		if (errors.length !== 0) {
			DisplayMessage(errors[0], 'error')
			return;
		}

		if (hasLoadingState) {
			setLoading(true);
		}

		const segmentsArray = buildSegmentsPayload(segments, segmentData);
		const requestBody = buildChecklistTemplateRequest(
			formData,
			segmentsArray,
			templateId
		);
		const requestData = JSON.stringify(requestBody);

		const result = await createChecklistTemplate(requestData);

		if (!result) {
			if (hasLoadingState) {
				setLoading(false);
			}

			return;
		}

		const savedTemplateId = result.id || templateId;

		if (savedTemplateId && isEditMode) {
			if (headerImageFile) {
				await handleSaveChecklistTemplateHeaderImage(savedTemplateId, headerImageFile);
			}
			else if (!headerImageData && initialHeaderImageData) {
				const deleteResult = await updateChecklistTemplateHeaderImage(savedTemplateId, null);
				if (deleteResult) {
					setInitialHeaderImageData(null);
				}
			}
		}

		const successMessage = isEditMode
			? resources["ChecklistTemplateWasUpdatedWithSuccess"]
			: resources["ChecklistTemplateWasCreatedWithSuccess"];

		if (showSuccessToasts) {
			DisplayMessage(successMessage, "success");
		}

		setTemplateWasCreated(true);
		if (hasLoadingState && !refetchTemplate) {
			setLoading(false);
		}

		if (!isEditMode) {
			setRedirect("/checklisttemplates");
			return;
		}

		if (refetchTemplate) {
			setTemplateWasUpdated(true);
		}
	};

	useEffect(() => {
		if (templateWasUpdated) {
			fetchTemplateDataById();
		}
	}, [templateWasUpdated, fetchTemplateDataById]);

	const onSaveNewItemsConfirmationModal = async () => {
		const hasLoadingState = false;
		const refetchTemplate = false;
		await handleSubmit(hasLoadingState, refetchTemplate);

		return await fetchTemplateDataById(hasLoadingState);
	};

	const history = useHistory();
	const initialFormData = useMemo(() => formData, [dataInitialized]);
	const initialSegments = useMemo(() => segments, [dataInitialized]);
	const initialSegmentData = useMemo(() => segmentData, [dataInitialized]);

	const hasUnsavedChanges = useMemo(() => {
		if (!dataInitialized) {
			return false;
		}

		return (
			JSON.stringify(formData) !== JSON.stringify(initialFormData) ||
			JSON.stringify(segments) !== JSON.stringify(initialSegments) ||
			JSON.stringify(segmentData) !== JSON.stringify(initialSegmentData)
		);
	}, [
		formData,
		segments,
		segmentData,
		initialFormData,
		initialSegments,
		initialSegmentData,
		dataInitialized,
	]);

	const navigateToTranslations = async () => {
		const hasLoadingState = false;
		const refetchTemplate = false;
		const showSuccessToasts = false;
		if (hasUnsavedChanges) {
			await handleSubmit(hasLoadingState, refetchTemplate, showSuccessToasts);
		}

		history.push(`/checklisttemplates/${templateId}/translations`);
	};

	return (
		<Box>
			{redirect && (
				<DelayedRedirect
					to={{
						pathname: redirect,
						state: { refreshGrid: templateWasCreated },
					}}
					delay={250}
				/>
			)}
			<Dialog
				fullScreen
				open={dialogOpen}
				onClose={handleClose}
				TransitionComponent={Transition}
			>
				<AppBarComponent
					idName="newChecklistTemplate"
					title={
						isEditMode
							? resources["EditChecklistTemplate"]
							: resources["NewChecklistTemplate"]
					}
					resources={resources}
					handleClose={handleClose}
					handleSubmit={handleSubmit}
				/>
				{loading ? (
					<Box className="centered-loader fadeIn">
						<CircularProgress />
					</Box>
				) : (
					<div className="container">
						<div className="py-4">
							<Paper elevation={3} className="p-4 mt-3 mb-3">
								<Box className="d-flex flex-column align-items-center">
									<div style={{ maxWidth: "800px", width: "100%" }}>
										<TextField
											id="description"
											style={{ width: "100%", marginBottom: "12px" }}
											value={formData.description}
											label={resources["Description"]}
											variant="outlined"
											name="description"
											required
											inputProps={{ maxLength: 64 }}
											onChange={(e) =>
												handleFormChange("description", e.target.value)
											}
											onBlur={() => handleFieldBlur("description")}
											error={descriptionError}
											helperText={
												descriptionError ? resources["FieldRequired"] : ""
											}
										/>

										<EnhancedDropdownComponent
											value={templateTypes[0].key}
											disabled
											options={templateTypes}
											labelText={resources["Type"]}
											id="type"
											placeholder={resources["SelectType"]}
										/>

										<EnhancedDropdownComponent
											value={formData.selectedType}
											handleChange={(e) =>
												handleFormChange(
													"selectedType",
													parseInt(e.target.value)
												)
											}
											options={GetSortedOptionsByName(objectTypes(resources))}
											labelText={resources["ObjectTypeLabel"]}
											id="objectType"
											placeholder={resources["SelectObjectType"]}
										/>

										<EnhancedDropdownComponent
											value={formData.brand}
											handleChange={(e) =>
												handleFormChange("brand", parseInt(e.target.value))
											}
											options={brands.map((brand) => ({
												key: brand.id,
												name: brand.description,
											}))}
											labelText={resources["Brand"]}
											id="brand"
											defaultValue={resources["SelectBrand"]}
										/>

										<Box className="d-flex" style={{ gap: "16px" }}>
											<Box style={{ flex: 1 }}>
												<EnhancedDropdownComponent
													value={formData.signatureOutletUserRoleID1}
													handleChange={(e) =>
														handleFormChange(
															"signatureOutletUserRoleID1",
															parseInt(e.target.value)
														)
													}
													options={roles.map((role) => ({
														key: role.id,
														name: role.description,
													}))}
													labelText={resources["OutletUserRole1"]}
													id="signatureOutletUserRoleID1"
													placeholder={resources["SelectRole"]}
												/>
											</Box>

											<Box style={{ flex: 1 }}>
												<EnhancedDropdownComponent
													value={formData.signatureOutletUserRoleID2}
													handleChange={(e) =>
														handleFormChange(
															"signatureOutletUserRoleID2",
															parseInt(e.target.value)
														)
													}
													options={roles.map((role) => ({
														key: role.id,
														name: role.description,
													}))}
													labelText={resources["OutletUserRole2"]}
													id="signatureOutletUserRoleID2"
													placeholder={resources["SelectRole"]}
												/>
											</Box>
										</Box>

										<Box className="d-flex mb-3" style={{ gap: "16px" }}>
											<DatePickerComponent
												id="dateValidFrom"
												label={resources["ValidFrom"]}
												value={formData.validFrom}
												onChange={(value) =>
													handleDateChange("validFrom", value)
												}
												onBlur={() => handleFieldBlur("validFrom")}
												error={validFromError}
												helperText={
													validFromError ? resources["FieldRequired"] : ""
												}
												minDate={new Date("2000-01-01")}
												maxDate={
													formData.validTill
														? new Date(formData.validTill)
														: undefined
												}
												required
												style={{ flex: 1 }}
											/>

											<DatePickerComponent
												id="dateValidTill"
												label={resources["ValidTill"]}
												value={formData.validTill}
												onChange={(value) =>
													handleDateChange("validTill", value)
												}
												minDate={
													formData.validFrom
														? new Date(formData.validFrom)
														: new Date("2000-01-01")
												}
												style={{ flex: 1 }}
											/>

											<TextField
												id="operationCode"
												value={formData.operationCode}
												label={resources["OperationCode"]}
												variant="outlined"
												name="operationCode"
												inputProps={{ maxLength: 50 }}
												onChange={(e) =>
													handleFormChange("operationCode", e.target.value)
												}
												style={{ flex: 1 }}
											/>
										</Box>

										<Box className="mt-3 mb-3">
											<Box className="d-flex" style={{ gap: "16px" }}>
												<CheckboxComponent
													label={resources["IsAutoSuggest"]}
													id="isAutosuggest"
													checked={formData.isAutosuggest}
													onChange={(e) =>
														handleFormChange("isAutosuggest", e.target.checked)
													}
												/>
												<Box style={{ flex: 1 }}>
													<CheckboxComponent
														label={resources["IsActive"]}
														id="isActive"
														checked={formData.isActive}
														onChange={(e) =>
															handleFormChange("isActive", e.target.checked)
														}
													/>
												</Box>
											</Box>

											<Box className="d-flex" style={{ gap: "16px" }}>
												<CheckboxComponent
													label={resources["IsB2CExcluded"]}
													id="isB2CExcluded"
													checked={formData.isB2CExcluded}
													onChange={(e) =>
														handleFormChange("isB2CExcluded", e.target.checked)
													}
												/>
												<CheckboxComponent
													label={resources["IsMandatory"] ?? "Is Mandatory"}
													id="isMandatory"
													checked={formData.isMandatory}
													onChange={(e) =>
														handleFormChange("isMandatory", e.target.checked)
													}
												/>
											</Box>
										</Box>

										{isEditMode && (
											<Box className="mt-3 mb-3">
												<ImageUpload
													imageData={headerImageData}
													removeImage={removeHeaderImage}
													handleFileChange={handleHeaderImageFileChange}
													file={headerImageFile}
													resources={resources}
													disabled={false}
													imageUriKey="imageUri"
													dropzoneTextKey="HeaderImage"
													imageAlt="header"
													imageHeight={150}
												/>
											</Box>
										)}

										<hr className="MuiDivider-root mb-4" />
										{isEditMode && (
											<Box
												display="flex"
												justifyContent="space-between"
												marginBottom="24px"
												alignItems="center"
											>
												<LanguageSwitch
													languages={translationsPercentages}
													selectedLang={lang}
													onChange={handleLanguageChange}
													id="edit-page"
												/>

												<Box display="flex" justifyContent="flex-end">
													<Button
														id="add-checklist-template-translations"
														variant="outlined"
														color="primary"
														style={{ height: "100%", marginLeft: 16 }}
														onClick={navigateToTranslations}
													>
														<i
															className="fas fa-plus mr-2"
															title={resources["AddTranslations"]}
														/>
														{resources["AddTranslations"]}
													</Button>
												</Box>
											</Box>
										)}

										<SegmentsCreation
											resources={resources}
											segments={segments}
											translationsData={translationsData}
											setSegments={setSegments}
											segmentData={segmentData}
											setSegmentData={setSegmentData}
											isEditMode={isEditMode}
											isTranslationsPreview={lang !== "BASE"}
											selectedLang={lang}
											onTranslationsModalClose={fetchTemplateDataById}
											onSaveNewItems={onSaveNewItemsConfirmationModal}
											validateFields={validateSegmentsAndItems}
											hasTranslationLicence={hasTranslationLicence}
										/>
									</div>
								</Box>
								{isEditMode &&
									(formData.modified.on || formData.created.on) && (
										<ModifiedByInfo
											className="mt-5"
											resources={resources}
											modifiedByAvatar={
												formData.modified.on
													? formData.modified.avatar
													: formData.created.avatar
											}
											modifiedOn={
												formData.modified.on
													? formData.modified.on
													: formData.created.on
											}
											modifiedByName={
												formData.modified.on
													? `${formData.modified.byFirstName} ${formData.modified.byLastName}`
													: `${formData.created.byFirstName} ${formData.created.byLastName}`
											}
											isCreated={formData.modified.on ? false : true}
											localeDate={true}
										/>
									)}
							</Paper>
						</div>
					</div>
				)}
			</Dialog>
		</Box>
	);
}
